import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './../../../reusable/styles.css';
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Empty,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import {
  callCentralBank,
  cancelCentralBankCall,
  CBRequest,
  hitCBResponse,
  ReusableInstrumentTableType,
  selectors,
  utils,
} from 'state';
import { formatters } from 'services';
import type { TableColumnsType as ColumnsType, TableColumnType as ColumnType } from 'antd';
import {
  currentPeriodInterbankDeals,
  hasActiveRequest,
  playerSentRFQRequests,
  underlyingAssets,
} from '../../../state/app/selectors';
import PortfolioLimits from '../PortfolioTab/PortfolioLimits';
import LimitsAndWeights from '../PortfolioTab/LimitsAndWeights';
import BenchmarkComposition from '../PortfolioTab/BenchmarkComposition';
import TradingWindows from '../PortfolioTab/TradingWindows';
import { BORROW, LEND, NOTHING } from '../../../state/app/client_money/types';
import InstrumentTable from './InstrumentTable';
import Auctions from '../Auctions';
import RepoContractsModal from '../RepoContractsModal';
import RepoModal from '../RepoModal';
import { fetchClientMoney } from '../../../state/app/client_money/actions';
import { fetchOptionsChains } from '../../../state/app/options/actions';
import AssetsTab from '../../AdminDashboard/Data/AssetsTab/AssetsTab';
import EquitiesTab from '../../AdminDashboard/Data/Equities';
import GeographyMarketHoursTab from '../../AdminDashboard/Data/GeographyMarketHoursTabe';
import DepositsTab from '../../AdminDashboard/Data/Deposits';
import PresetsTab from '../../AdminDashboard/Data/PresetsTab';
import CurrenciesTab from '../../AdminDashboard/Data/CurrenciesTab';
import IndexFundsTab from '../../AdminDashboard/Data/IndexFundsTab/IndexFundsTab';
import NewsTab from '../../AdminDashboard/Data/NewsTab';
import ClientDealing from '../../AdminDashboard/Data/ClientDealing';
import FXCrossTable from '../../AdminDashboard/Data/FXCrossTable';
import SwapPointsTab from '../../AdminDashboard/Data/SwapPointsTab';

type quoteState = {
  [id: number]: { bid: number; ask: number };
};

const CentralBankPlayerTab: React.FC = () => {
  const dispatch = useDispatch();
  const { id: player_id } = useSelector(selectors.player);
  const session = useSelector(selectors.activeSession);

  const currencies = useSelector(selectors.currencies);
  const wallets = useSelector(selectors.wallets);
  const reportingCurrency = utils.useReportingCurrency(session.id);

  const reportingWallet = wallets.find(
    wallet => currencies[wallet.currency] === reportingCurrency
  )!;
  const clients_fetched = useSelector(selectors.clientsFetched);
  const clientMoney = useSelector(selectors.getClientMoney);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const _financialInstruments = useSelector(selectors.financialInstrumentsList);
  const reportingWalletAmount = reportingWallet.amount;
  const [quoteAmount, setQuoteAmount] = useState<number>(0);

  useEffect(() => {
    if (session.client_money) {
      if (clients_fetched) return;
      dispatch(fetchClientMoney({ session_id: session.id }));
    }
  }, [dispatch]);

  const repoInstruments = _financialInstruments.filter(
    fin => underlyingAssets[fin.underlying_asset].resourcetype === 'Repo'
  );
  const repoDataSource = repoInstruments.map(fin => {
    const asset = underlyingAssets[fin.underlying_asset];
    return {
      ...fin,
      ...asset,
      currency: currencies[asset.base_currency].symbol,
    };
  });
  const [instrument, setInstrument] = useState(repoInstruments[0]);

  const [visibleRepoModal, setVisibleRepoModal] = useState(false);
  const [visibleRepoContractsModal, setVisibleRepoContractsModal] =
    useState(false);
  const hasActiveCBRequest = useSelector(selectors.hasActiveCBRequest);

  const pendingCentralBankResponses = useSelector(
    selectors.pendingCentralBankResponses
  );
  const activeCentralBankDepos = useSelector(selectors.activeCentralBankDepos);
  const pastCentralBankDepos = useSelector(selectors.pastCentralBankDepos);

  const CentralBankQuotesColumns: ColumnType<CBRequest>[] = [
    {
      title: 'Bank',
      dataIndex: 'responding_player_name',
      key: 'responding_player_name',
      render: (_: any, record) => 'CB',
      defaultSortOrder: 'ascend',
    },
    {
      title: `Amount, ${reportingCurrency.symbol}`,
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => formatters.commasNoDigits(val),
    },
    {
      title: 'Bid, %',
      dataIndex: 'bid',
      key: 'bid',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 250,
      render: function Actions(_: any, record: CBRequest) {
        // CB Responses
        return (
          <span>
            <Button
              // type="link"
              style={{ marginRight: 5 }}
              onClick={() => {
                console.log('send response');
                dispatch(
                  hitCBResponse({
                    response_id: record.id,
                    action: LEND,
                    player_id: player_id,
                  })
                );
              }}
            >
              Deposit
            </Button>
            <Button
              // type="link"
              style={{ marginRight: 5 }}
              onClick={() => {
                console.log('send response');
                dispatch(
                  hitCBResponse({
                    response_id: record.id,
                    action: NOTHING,
                    player_id: player_id,
                  })
                );
              }}
            >
              Cancel
            </Button>
          </span>
        );
      },
    },
  ];

  const CentralBankActiveDeposColumns: ColumnType<CBRequest>[] = [
    {
      title: 'Bank',
      dataIndex: 'responding_player_name',
      key: 'responding_player_name',
      render: (_: any, record) => 'CB',
      defaultSortOrder: 'ascend',
    },
    {
      title: `Amount, ${reportingCurrency.symbol}`,
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => formatters.commasNoDigits(val),
    },
    {
      title: 'Bid, %',
      dataIndex: 'bid',
      key: 'bid',
    },
    {
      title: `Interest, ${reportingCurrency.symbol}`,
      dataIndex: 'interest',
      key: 'interest',
      // width: 250,
      render: (val: any, record: CBRequest) => {
        const interest =
          record.amount * (record.bid / 100) * (session.frequency / 365);
        return formatters.commasNoDigits(interest);
      },
    },
    {
      title: 'Maturity, days',
      dataIndex: 'maturity',
      key: 'maturity',
      // width: 50,
      render: (val: any, record: CBRequest) => {
        return session.frequency;
      },
    },
  ];

  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          {`No interbank requests at the moment.`}
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };

  const noDataResponses = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          {`Call the central bank to get a quote.`}
        </Typography.Text>
      }
    />
  );

  const noDataBehaviourResponses = {
    emptyText: noDataResponses,
  };

  const centralBankInfo = (
    <div>
      <p>
        The central bank will offer different quotes based on your available
        cash amount.
      </p>
      <ul>
        <li>Negative balance → emergency lending quote</li>
        <li>Positive, but lower than MRR → MRR shortfall quote</li>
      </ul>
    </div>
  );

  const labelColWidth = '160px';

  const customLombardStyle =
    reportingWallet.amount < 0
      ? { marginTop: 12, color: 'red' }
      : { marginTop: 12 };

  const repoColumns: ColumnType<any>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'BBG Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
    },
    {
      title: 'CCY',
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: 'ASK',
      dataIndex: 'ask',
      render: function RepoAsk(val: any, record: ReusableInstrumentTableType) {
        return <div>{formatters.commasExact2Digits(val)} %</div>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: function Action(text, record) {
        return (
          <div>
            <Button
              onClick={() => setVisibleRepoModal(true)}
              style={{ marginRight: 5 }}
            >
              Show options
            </Button>
            <Button onClick={() => setVisibleRepoContractsModal(true)}>
              Show contracts
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/*<Typography.Title level={4}>TBill Auctions</Typography.Title>*/}
      {/*<Auctions />*/}
      {/*<Row style={{ flexFlow: 'row nowrap' }} gutter={40}></Row>*/}
      <Row style={{ flexFlow: 'row nowrap' }} gutter={40}>
        <Col span={14} style={{ minWidth: 900 }}>
          {/*<Typography.Title level={4}>TBill Auctions</Typography.Title>*/}
          <div>
            <Card title={'Lombard borrowing (repo)'}>
              <Table
                pagination={false}
                bordered
                dataSource={repoDataSource}
                columns={repoColumns}
              />
            </Card>
            <br />
            <Card title={'TBill Auctions'}>
              <Auctions />
            </Card>
          </div>
        </Col>
        <Col
          span={10}
          style={{
            width: 'min-content',
            display: 'flex',
            flexDirection: 'column',
            // gap: 40,
            borderLeft: '1px solid #eeeeee',
          }}
        >
          <div>
            <Card title={'Deposits'} style={{ minWidth: 600 }}>
              {/*</Card>*/}
              {/*<Card*/}
              {/*  size="small"*/}
              {/*  // title={'Launch RFQ'}*/}
              {/*  // style={{ width: 400 }}*/}
              {/*>*/}
              <Row gutter={[20, 20]}>
                <Col
                  flex={labelColWidth}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'right',
                  }}
                >
                  <Typography.Text style={{ marginTop: 5 }}>
                    Amount:{' '}
                  </Typography.Text>
                </Col>
                <Col>
                  <InputNumber
                    value={quoteAmount}
                    onChange={value => {
                      if (typeof value === 'number') setQuoteAmount(value);
                    }}
                    formatter={formatters.commasNoDigits}
                    style={{ width: 120 }}
                    step={1_000_000}
                    // max={Math.max(0, reportingWalletAmount)}
                    min={0}
                  />
                </Col>
                <Col>
                  <div style={{ marginTop: 5 }}>
                    <Typography.Text type={'secondary'}>
                      (Available:{' '}
                      {formatters.commasNoDigits(reportingWalletAmount)}{' '}
                      {reportingCurrency.symbol})
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
              <Row gutter={[20, 20]}>
                <Col
                  flex={labelColWidth}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'right',
                  }}
                >
                  <Typography.Text style={{ marginTop: 5 }}>
                    Get deposit quote:{' '}
                  </Typography.Text>
                </Col>
                <Col>
                  <Button
                    // className={'loading'}
                    type="primary"
                    onClick={() => {
                      dispatch(
                        callCentralBank({
                          amount: quoteAmount,
                          player_id: player_id,
                          // selected_players: selectedPlayers,
                        })
                      );
                    }}
                    style={{ width: 200 }}
                    // disabled={hasActiveCBRequest}
                  >
                    <div style={{ fontFamily: 'monospace' }}>
                      Call Central Bank
                    </div>
                    {/*<div className={'loading'}>Central Bank...</div>*/}
                  </Button>
                  {/*  //   <div className={'loading'}>Central Bank...</div>*/}
                  {/*  // </div>*/}
                  {/*// )}*/}
                  <Popover
                    placement={'right'}
                    content={centralBankInfo}
                    title="Info"
                  >
                    <InfoCircleOutlined
                      style={{ marginTop: 14, marginLeft: 10 }}
                    />
                  </Popover>
                </Col>
              </Row>

              <Tabs defaultActiveKey="quotes">
                <Tabs.TabPane
                  key="quotes"
                  tab={
                    <Badge
                      count={pendingCentralBankResponses.length}
                      offset={[10, -5]}
                    >
                      Quotes
                    </Badge>
                  }
                >
                  <Table
                    pagination={false}
                    bordered
                    // title={() => 'Quotes'}
                    columns={CentralBankQuotesColumns}
                    rowKey={r => `$${r.id}`}
                    dataSource={pendingCentralBankResponses}
                    locale={noDataBehaviourResponses}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane
                  key="adctive_deals"
                  tab={
                    <Badge
                      count={activeCentralBankDepos.length}
                      offset={[10, -5]}
                    >
                      Active Depos
                    </Badge>
                  }
                >
                  <Table
                    pagination={{ defaultPageSize: 10 }}
                    bordered
                    // title={() => 'Quotes'}
                    columns={CentralBankActiveDeposColumns}
                    rowKey={r => `$${r.id}`}
                    dataSource={activeCentralBankDepos}
                    locale={noDataBehaviourResponses}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane key="past_deals" tab="Past depos">
                  <Table
                    pagination={{ defaultPageSize: 10 }}
                    bordered
                    // title={() => 'Quotes'}
                    columns={CentralBankActiveDeposColumns}
                    rowKey={r => `$${r.id}`}
                    dataSource={pastCentralBankDepos}
                    locale={noDataBehaviourResponses}
                  />
                </Tabs.TabPane>
              </Tabs>

              <br />
              {/*{activeRequest && (*/}
              {/*  <div>*/}
              {/*    Your active RFQ:{' '}*/}
              {/*    {formatters.commasNoDigits(activeRequest.amount)}{' '}*/}
              {/*    {reportingCurrency.symbol}*/}
              {/*    <Button*/}
              {/*      onClick={() => {*/}
              {/*        dispatch(cancelRFQ({ request_id: activeRequest.id }));*/}
              {/*      }}*/}
              {/*    >*/}
              {/*      Cancel RFQ*/}
              {/*    </Button>*/}
              {/*  </div>*/}
              {/*)}*/}
              {/*<Table*/}
              {/*  pagination={false}*/}
              {/*  bordered*/}
              {/*  // title={() => 'Quotes'}*/}
              {/*  columns={CentralBankQuotesColumns}*/}
              {/*  rowKey={r => `$${r.id}`}*/}
              {/*  dataSource={pendingCentralBankResponses}*/}
              {/*  locale={noDataBehaviourResponses}*/}
              {/*/>*/}
            </Card>
            <br />
            <Card title={'Emergency Borrowing'}>
              {/*<Typography.Title level={4}>Emergency borrowing</Typography.Title>*/}
              <div>
                <div>
                  Overdraft (Negative Available Amount) at the end of the period
                  will be automatically covered at the emergency rate.
                </div>
                <div style={customLombardStyle}>
                  Emergency rate:{' '}
                  {formatters.commas2Digits(clientMoney.lombard_rate)}%
                </div>{' '}
                Total emergency interest paid:{' '}
                {formatters.commasNoDigits(reportingWallet.lombard_interest)}{' '}
                {reportingCurrency.symbol}
                {/*<Popover placement={'top'} content={lombardInfo} title="Info">*/}
                {/*  <InfoCircleOutlined style={{ marginTop: 14 }} />*/}
                {/*</Popover>*/}
              </div>
            </Card>
          </div>
        </Col>
      </Row>
      {visibleRepoContractsModal && (
        <RepoContractsModal
          visible={visibleRepoContractsModal}
          hide={() => setVisibleRepoContractsModal(false)}
          id={instrument.id}
        />
      )}
      {visibleRepoModal && (
        <RepoModal
          visible={visibleRepoModal}
          hide={() => setVisibleRepoModal(false)}
          id={instrument.id}
        />
      )}
    </div>
  );
};

export default CentralBankPlayerTab;
